import React from 'react';
import Loading from 'src/components/Loading';

import { myVPOActionListNameFieldsType, VPOfactoryList } from '../../../api/vpo.config';
import { filterDefault } from '../../../types/typesVPO';
import { TooltipItem } from 'src/utils/Tooltip';

interface TableRowProps {
  FilteGroup: myVPOActionListNameFieldsType[typeof VPOfactoryList][];
  FilterColumns: filterDefault[];
  reportsData?: any;
  reportsLoading?: boolean;
}

type StatusType = 'Завершено' | 'В процессе' | 'Не начато';

type ColumnResult = {
  status: string;
  percentage: number;
};

export default function TableRow({ FilteGroup, FilterColumns, reportsData, reportsLoading }: TableRowProps) {
  const getStatusMarkerColor = (status: string) => {
    switch (status) {
      case 'Завершено':
        return 'bg-[#22AD5C]';
      case 'В процессе':
        return 'bg-[#F2A900]';
      default:
        return 'bg-[#8899A8]';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Завершено':
        return 'bg-[#22AD5C]';
      case 'В процессе':
        return 'bg-[#F2A900]';
      default:
        return 'bg-[#8899A8]';
    }
  };

  const getColumnStatus = (factoryId: string, columnId: string): ColumnResult => {
    if (reportsData?.data?.[factoryId]?.directions?.[columnId]) {
      const directionData = reportsData.data[factoryId].directions[columnId];
      return {
        status: directionData.status || 'Не начато',
        percentage: directionData.percentage || 0,
      };
    }

    return {
      status: 'Не начато',
      percentage: 0,
    };
  };

  const getRowStatus = (statuses: string[]): StatusType => {
    if (statuses.every((status) => status === 'Завершено')) return 'Завершено';
    if (statuses.every((status) => status === 'Не начато')) return 'Не начато';
    if (statuses.some((status) => status === 'Завершено') || statuses.some((status) => status === 'В процессе')) return 'В процессе';
    return 'Не начато';
  };

  const Styles = {
    column: 'w-[12%] flex items-center justify-center px-4 py-2 rounded text-white text-sm font-medium',
    thTable: 'py-3 px-3 text-left text-sm font-medium text-black ',
  };

  //! Сортировка колон по индексу
  const sortedColumns = React.useMemo(() => {
    if (!FilterColumns || FilterColumns.length === 0) return [];
    return [...FilterColumns].sort((a: any, b: any) => (a.index || 0) - (b.index || 0));
  }, [FilterColumns]);

  const calculateTotalAverage = (factoryId: string): number => {
    if (reportsData?.data?.[factoryId]?.averagePercentage !== undefined) {
      return reportsData.data[factoryId].averagePercentage;
    }

    return 0;
  };

  return (
    <div className='h-[700px] overflow-auto relative scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] rounded-lg'>
      <div className='w-full shadow-md sm:rounded-lg'>
        <div className='relative'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='sticky top-0 z-10 bg-gray-50'>
              <tr>
                <th scope='col' className={`${Styles.thTable} whitespace-nowrap`}>
                  Статус
                </th>
                <th scope='col' className={`${Styles.thTable} whitespace-nowrap`}>
                  Завод
                </th>
                <th scope='col' className={`${Styles.thTable} whitespace-nowrap`}>
                  Группа
                </th>
                <th scope='col' className={`${Styles.thTable} min-w-[160px]`}>
                  Среднее по заводу
                </th>
                <th scope='col' className={`${Styles.thTable}`}>
                  Статус по колоннам
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 overflow-y-auto'>
              {FilteGroup.map((factory: any) => {
                const columnResults = sortedColumns.map((column: any) => {
                  return getColumnStatus(factory.id, column.id || column.ID);
                });

                const rowStatus = reportsData?.data?.[factory.id]?.status || getRowStatus(columnResults.map((r: ColumnResult) => r.status));

                const average = calculateTotalAverage(factory.id);

                return (
                  <tr key={factory.ID || factory.id}>
                    <td className='h-full'>
                      <div className='flex items-center gap-2 h-full py-4 px-4 text-sm font-normal text-primary whitespace-nowrap'>
                        <div className={`w-3 h-3 rounded ${getStatusMarkerColor(rowStatus)}`}></div>
                        {rowStatus}
                      </div>
                    </td>
                    <td className='py-4 px-4 text-sm text-[#637381]'>{factory.title}</td>
                    <td className='py-4 px-4 text-sm text-[#637381]'>{factory.group?.title || 'Не указано'}</td>
                    <td className='py-4 px-4'>
                      <div className='w-full max-w-xs'>
                        <div className='text-xs font-medium text-[#637381] mb-1'>Текущая оценка</div>
                        <div className='w-full bg-gray-200 h-5 rounded-full relative'>
                          <div
                            className={`h-full rounded-full flex items-center justify-center text-xs text-white font-normal ${getStatusColor(rowStatus)} transition-all duration-300`}
                            style={{ width: `${average}%` }}
                          ></div>
                          <div className='absolute inset-0 flex items-center justify-center text-sm text-gray-600'>{average.toFixed(0)}%</div>
                        </div>
                      </div>
                    </td>
                    <td className='h-full'>
                      <div className={`grid gap-x-2 h-full py-4 px-4 w-full`} style={{ gridTemplateColumns: `repeat(${sortedColumns.length}, minmax(0, 1fr))` }}>
                        {columnResults.map((result: ColumnResult, index: number) => (
                          // <TooltipItem position='top' tooltipsText={`${result.percentage}%`} key={sortedColumns[index]?.id || sortedColumns[index]?.ID || index}>
                          // <span
                          //   className={`${Styles.column} ${getStatusColor(result.status)} w-auto`}
                          // >
                          //   {sortedColumns[index]?.code || sortedColumns[index]?.title ||  ''}
                          // </span>
                          // </TooltipItem>

                          <span key={sortedColumns[index]?.id || sortedColumns[index]?.ID || index} className={`${Styles.column} ${getStatusColor(result.status)} w-auto`}>
                            {sortedColumns[index]?.code || sortedColumns[index]?.title || ''}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {FilteGroup.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <div className='h-[600px] flex items-center justify-center bg-white rounded-lg shadow'>
                      <Loading />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
